import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export type TailwindClasses = Parameters<typeof clsx>[0];

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function resolveTailwindVariantValue<T extends Record<string, unknown>>(
  obj: T,
  value: unknown,
): keyof T | undefined {
  if (typeof value === "boolean") {
    return value ? (value as unknown as keyof T) : undefined;
  }

  if (typeof value === "string" || typeof value === "number") {
    return Object.hasOwn(obj, value) ? (value as keyof T) : undefined;
  }

  return undefined;
}

export function resolveTailwindVariantBoolean<
  T extends Record<string, unknown>,
>(obj: T, value: unknown): boolean {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string" || typeof value === "number") {
    return !!Object.hasOwn(obj, value);
  }

  return false;
}

export function extendTVSlot<T, K extends keyof T>(
  slotName: K,
  className?: string | T,
): string | T[K] | undefined {
  if (typeof className === "string") {
    return className;
  }

  return className?.[slotName];
}

/**
 * Like {@link extendTVSlot}, but normalizes the result to the nested slots
 * object form. A string value (either the parent `className` itself or the
 * slot value) is treated as the `root` sub-slot.
 */
export function extendTVSlots<T, K extends keyof T>(
  slotName: K,
  className?: string | T,
): Exclude<T[K], string> | undefined {
  const value = extendTVSlot(slotName, className);

  if (value === undefined) {
    return undefined;
  }

  if (typeof value === "string") {
    return { root: value } as Exclude<T[K], string>;
  }

  return value as Exclude<T[K], string>;
}

export function removeDuplicates(values: string[]) {
  return [...new Set(values)];
}

export function removeEmptyStrings(values: string[]) {
  return values.filter((value) => value.length > 0);
}

export function trimStrings(values: string[]) {
  return values.map((value) => value.trim());
}

export function addQuotesToStrings(values: string[]) {
  return values.map((value) => `"${value}"`).join(" ");
}

/**
 * `keyof` on a tv() variants object is always strings (`"true"`, `"1"`).
 * Tailwind Variants wants the original literals: `true` / `false` / `1`.
 */
export type TailwindVariantKey<T> = {
  [K in keyof T]: K extends "true"
    ? true
    : K extends "false"
      ? false
      : K extends `${infer N extends number}`
        ? N
        : K;
}[keyof T];

export function isValidTailwindVariantKey<T extends Record<string, unknown>>(
  obj: T,
  value: unknown,
): value is TailwindVariantKey<T> {
  if (typeof value === "boolean") {
    return Object.hasOwn(obj, String(value));
  }

  if (typeof value === "string" || typeof value === "number") {
    return Object.hasOwn(obj, value);
  }

  return false;
}

export function validTailwindVariantKey<T extends Record<string, unknown>>(
  obj: T,
  value: unknown,
): TailwindVariantKey<T> | undefined {
  if (!isValidTailwindVariantKey(obj, value)) {
    return undefined;
  }

  return value;
}
